/**
 * Testimonials Block JavaScript
 * Slick slider + HTML5 / Vimeo / YouTube video playback on play or card tap.
 */

import { isBlockOnPage, whenVisible } from "../../src/js/modules/block-present.js";

(function () {
  "use strict";

  if (!isBlockOnPage("testimonials")) {
    return;
  }

  function isHeavyMobileVideo(url) {
    return /(?:^|[^\d])2160(?:p)?(?:[^\d]|$)|3840x|\/4k(?:\/|$)|rendition\/2160/i.test(
      String(url || ""),
    );
  }

  function bindFileSrc(video) {
    if (!(video instanceof HTMLVideoElement)) {
      return;
    }
    if (video.getAttribute("src") || video.querySelector("source[src]")) {
      return;
    }
    const src = (video.dataset.videoFileSrc || "").trim();
    if (!src) {
      return;
    }
    if (
      isHeavyMobileVideo(src) &&
      window.matchMedia("(max-width: 767px)").matches
    ) {
      return;
    }
    const type = (video.dataset.videoFileType || "").trim();
    const source = document.createElement("source");
    source.src = src;
    if (type) {
      source.type = type;
    }
    video.appendChild(source);
  }

  function bindFileSrcForPlayback(video) {
    if (!(video instanceof HTMLVideoElement)) {
      return;
    }
    if (video.getAttribute("src") || video.querySelector("source[src]")) {
      return;
    }
    const src = (video.dataset.videoFileSrc || "").trim();
    if (!src) {
      return;
    }
    const type = (video.dataset.videoFileType || "").trim();
    const source = document.createElement("source");
    source.src = src;
    if (type) {
      source.type = type;
    }
    video.appendChild(source);
    video.load();
  }

  function getVideoEmbedUrl(url) {
    if (!url) return null;

    try {
      const parsed = new URL(url);
      const hostname = parsed.hostname.toLowerCase();
      const pathSegments = parsed.pathname
        .split("/")
        .filter((segment) => segment !== "");

      // Vimeo: vimeo.com/ID, vimeo.com/ID/HASH, player.vimeo.com/video/ID
      if (hostname.includes("vimeo.com")) {
        let videoId = "";
        let privacyH = parsed.searchParams.get("h") || "";

        if (hostname.includes("player.vimeo.com")) {
          if (pathSegments[0] === "video" && pathSegments[1]) {
            videoId = pathSegments[1];
          }
        } else if (pathSegments[0] === "video" && pathSegments[1]) {
          videoId = pathSegments[1];
        } else if (/^\d+$/.test(pathSegments[0] || "")) {
          videoId = pathSegments[0];
          if (pathSegments[1] && /^[a-zA-Z0-9]+$/.test(pathSegments[1])) {
            privacyH = privacyH || pathSegments[1];
          }
        }

        if (videoId) {
          const params = new URLSearchParams({
            autoplay: "1",
            muted: "0",
            title: "0",
            byline: "0",
            portrait: "0",
            dnt: "1",
            playsinline: "1",
          });
          if (privacyH) {
            params.set("h", privacyH);
          }
          return `https://player.vimeo.com/video/${encodeURIComponent(
            videoId,
          )}?${params.toString()}`;
        }
      }

      // YouTube
      if (hostname.includes("youtube.com") || hostname.includes("youtu.be")) {
        let videoId = null;
        if (parsed.searchParams.has("v")) {
          videoId = parsed.searchParams.get("v");
        } else if (hostname.includes("youtu.be") && pathSegments[0]) {
          videoId = pathSegments[0];
        } else if (pathSegments[0] === "embed" && pathSegments[1]) {
          videoId = pathSegments[1];
        } else if (pathSegments[0] === "v" && pathSegments[1]) {
          videoId = pathSegments[1];
        }

        if (videoId) {
          videoId = videoId.split("?")[0].split("&")[0];
          return `https://www.youtube.com/embed/${encodeURIComponent(
            videoId,
          )}?autoplay=1&rel=0&playsinline=1`;
        }
      }
    } catch (error) {
      return null;
    }

    return null;
  }

  function initTestimonialVideosInBlock(block, sliderEl) {
    const testimonialMembers = sliderEl.querySelectorAll(
      ".single_testimonial_member",
    );
    const mediaByKey = new Map();
    let mediaUid = 0;

    function pauseAll(exceptKey = null) {
      mediaByKey.forEach((entry, key) => {
        if (key === exceptKey) return;
        if (entry.type === "file" && entry.video) {
          entry.video.pause();
        }
      });
    }

    function clearEmbed(videoContainer) {
      const iframe = videoContainer.querySelector("iframe.testimonial-embed");
      if (iframe) {
        iframe.remove();
      }
    }

    function fadeOutPlayingUi(
      member,
      videoContainer,
      profileImageWrapper,
      playButton,
    ) {
      if (!videoContainer) return;
      videoContainer.classList.remove("playing");
      videoContainer.style.transition = "opacity 0.5s ease";
      videoContainer.style.opacity = "0";
      setTimeout(() => {
        videoContainer.style.display = "none";
        clearEmbed(videoContainer);
      }, 500);
      if (profileImageWrapper) {
        profileImageWrapper.style.transition = "opacity 0.5s ease";
        profileImageWrapper.style.opacity = "0";
        profileImageWrapper.style.pointerEvents = "";
        setTimeout(() => {
          profileImageWrapper.style.opacity = "1";
        }, 100);
      }
      if (playButton) {
        playButton.style.transition = "opacity 0.5s ease";
        playButton.style.opacity = "0";
        playButton.style.pointerEvents = "";
        setTimeout(() => {
          playButton.style.opacity = "1";
        }, 100);
      }
      member.classList.remove("playing");
    }

    function resetPlayingMember(member) {
      if (!member.classList.contains("playing")) return;
      const videoContainer = member.querySelector(
        ".team_members--member__video",
      );
      const video = videoContainer?.querySelector(
        "video.testimonial-html5-video",
      );
      const profileImageWrapper = member.querySelector(
        ".profile_image_wrapper",
      );
      const playButton = member.querySelector(".play_button");
      if (video) {
        video.pause();
      }
      fadeOutPlayingUi(
        member,
        videoContainer,
        profileImageWrapper,
        playButton,
      );
    }

    function resetAllPlayingInBlock() {
      block.querySelectorAll("video.testimonial-html5-video").forEach((v) => {
        v.pause();
      });
      block
        .querySelectorAll(".single_testimonial_member.playing")
        .forEach(resetPlayingMember);
    }

    testimonialMembers.forEach((member) => {
      const videoContainer = member.querySelector(
        ".team_members--member__video",
      );
      const profileImageWrapper = member.querySelector(
        ".profile_image_wrapper",
      );
      const playButton = member.querySelector(".play_button");
      const video = videoContainer?.querySelector(
        "video.testimonial-html5-video",
      );
      const videoType = videoContainer?.getAttribute("data-video-type") || "";
      const videoUrl = videoContainer?.getAttribute("data-video-url") || "";

      if (!videoContainer || !playButton) return;
      if (videoType === "file" && !video) return;
      if (videoType === "embed" && !videoUrl) return;
      if (!videoType) return;

      const mediaKey = `testimonial-media-${mediaUid++}`;

      function ensureClickOverlay() {
        let overlay = videoContainer.querySelector(".video-click-overlay");
        if (overlay) return;
        overlay = document.createElement("div");
        overlay.className = "video-click-overlay";
        overlay.setAttribute("aria-hidden", "true");
        overlay.addEventListener("click", function (e) {
          e.stopPropagation();
          if (!member.classList.contains("playing")) return;
          if (video) {
            video.pause();
          }
          fadeOutPlayingUi(
            member,
            videoContainer,
            profileImageWrapper,
            playButton,
          );
        });
        videoContainer.appendChild(overlay);
      }

      function memberIsCurrentCarouselSlide() {
        const slide = member.closest(".slick-slide");
        if (!slide) return true;
        return slide.classList.contains("slick-current");
      }

      function mountEmbed() {
        clearEmbed(videoContainer);
        const embedUrl = getVideoEmbedUrl(videoUrl);
        if (!embedUrl) {
          console.warn(
            "[BLOCK] testimonials: could not build embed URL from",
            videoUrl,
          );
          return false;
        }

        const iframe = document.createElement("iframe");
        iframe.className = "testimonial-embed";
        iframe.src = embedUrl;
        iframe.setAttribute("title", "Testimonial video");
        iframe.setAttribute("allow", "autoplay; fullscreen; picture-in-picture");
        iframe.setAttribute("allowfullscreen", "");
        iframe.setAttribute("frameborder", "0");
        videoContainer.insertBefore(iframe, videoContainer.firstChild);
        return true;
      }

      function startPlayback() {
        if (!memberIsCurrentCarouselSlide()) {
          return;
        }
        if (member.classList.contains("playing")) {
          return;
        }

        pauseAll(mediaKey);
        mediaByKey.set(mediaKey, { type: videoType, video: video || null });
        member.classList.add("playing");
        videoContainer.classList.add("playing");

        if (profileImageWrapper) {
          profileImageWrapper.style.transition = "opacity 0.5s ease";
          profileImageWrapper.style.opacity = "0";
          profileImageWrapper.style.pointerEvents = "none";
        }
        playButton.style.transition = "opacity 0.5s ease";
        playButton.style.opacity = "0";
        playButton.style.pointerEvents = "none";

        videoContainer.style.display = "block";
        videoContainer.style.opacity = "0";
        videoContainer.style.transition = "opacity 0.5s ease";
        ensureClickOverlay();

        if (videoType === "embed") {
          if (!mountEmbed()) {
            fadeOutPlayingUi(
              member,
              videoContainer,
              profileImageWrapper,
              playButton,
            );
            return;
          }
        }

        setTimeout(() => {
          videoContainer.style.opacity = "1";
          if (videoType === "file" && video) {
            bindFileSrcForPlayback(video);
            const playPromise = video.play();
            if (playPromise && typeof playPromise.catch === "function") {
              playPromise.catch((err) => {
                console.warn("[BLOCK] testimonials: HTML5 play failed", err);
              });
            }
          }
        }, 100);
      }

      playButton.addEventListener("click", function (e) {
        e.stopPropagation();
        startPlayback();
      });

      /** Whole card tap = play; ignore obvious drags so Slick swipes still feel right */
      let pointerStart = null;
      member.addEventListener("pointerdown", function (e) {
        if (e.button !== 0) return;
        if (e.target.closest(".play_button")) return;
        pointerStart = { x: e.clientX, y: e.clientY };
      });
      member.addEventListener("pointercancel", function () {
        pointerStart = null;
      });
      member.addEventListener("click", function (e) {
        if (e.button !== 0) return;
        if (pointerStart) {
          const moved = Math.hypot(
            e.clientX - pointerStart.x,
            e.clientY - pointerStart.y,
          );
          pointerStart = null;
          if (moved > 20) return;
        }
        e.stopPropagation();
        startPlayback();
      });
    });

    return resetAllPlayingInBlock;
  }

  function prefetchNearbyVideos(block) {
    block.querySelectorAll("video.testimonial-html5-video").forEach((video) => {
      bindFileSrc(video);
    });
  }

  function initTestimonials() {
    const blocks = document.querySelectorAll(".testimonials");
    if (!blocks.length) return;

    if (!window.jQuery || typeof window.jQuery.fn.slick === "undefined") {
      console.warn("[BLOCK] testimonials: jQuery or Slick not available");
      return;
    }

    const $ = window.jQuery;

    blocks.forEach((block) => {
      const slider = block.querySelector("[data-testimonials-slider]");
      const arrowsWrap = block.querySelector("[data-testimonials-arrows]");
      if (!slider || !slider.querySelector(".testimonial-slide")) return;

      if ($(slider).hasClass("slick-initialized")) return;

      const prevBtn = arrowsWrap
        ? arrowsWrap.querySelector(".testimonials-prev")
        : null;
      const nextBtn = arrowsWrap
        ? arrowsWrap.querySelector(".testimonials-next")
        : null;

      const blockId = slider.getAttribute("data-testimonials-slider");
      const paginationNav = blockId
        ? block.querySelector(`[data-testimonials-pagination="${blockId}"]`)
        : null;

      const baseSettings = {
        dots: false,
        arrows: true,
        prevArrow: prevBtn ? $(prevBtn) : undefined,
        nextArrow: nextBtn ? $(nextBtn) : undefined,
        infinite: false,
        speed: 500,
        slidesToShow: 1,
        slidesToScroll: 1,
        fade: false,
        cssEase: "ease-out",
        autoplay: false,
        adaptiveHeight: false,
      };

      const mobileSliderSettings =
        paginationNav &&
        slider.querySelectorAll(".testimonial-slide").length > 1
          ? {
              dots: true,
              arrows: false,
              appendDots: $(paginationNav),
              customPaging(slick, i) {
                const total = slick.slideCount;
                const label = `Slide ${i + 1} of ${total}`;
                return `<button type="button" class="team-slider-pagination__dot" aria-label="${label}"></button>`;
              },
            }
          : {
              dots: false,
              arrows: false,
            };

      let resetPlayingVideos = function () {};

      $(slider)
        .slick({
          ...baseSettings,
          responsive: [
            {
              breakpoint: 768,
              settings: mobileSliderSettings,
            },
          ],
        })
        .on("beforeChange", function (event, slick, currentSlide, nextSlide) {
          if (currentSlide !== nextSlide) {
            resetPlayingVideos();
          }
        });

      resetPlayingVideos = initTestimonialVideosInBlock(block, slider);
      whenVisible(block, () => prefetchNearbyVideos(block), {
        rootMargin: "400px 0px",
      });
    });
  }

  function bootTestimonials() {
    whenVisible(".testimonials", initTestimonials, { rootMargin: "400px 0px" });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", bootTestimonials);
  } else {
    bootTestimonials();
  }
})();
